import React, { useEffect, useState } from 'react';
import AmbassadorApplyForm from './AmbassadorApplyForm';
import { track } from '../services/analytics';
import './AmbassadorsPage.css';

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * COPY REVIEW: LOOKING_FOR and FAQS below are still a first draft — rewrite
 * them before this page goes live. PERKS copy is final (supplied).
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Shared wrapper so all three icons keep the same stroke weight and metrics.
const PerkIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    className="amb-perk-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const PERKS: { icon: React.ReactNode; title: string; body: string }[] = [
  {
    // Unlock — unlimited access
    icon: (
      <PerkIcon>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 9.9-1" />
      </PerkIcon>
    ),
    title: 'Run with Rally',
    body: 'Get unlimited access to Rally. Be the first to test out our latest features.',
  },
  {
    // Gift — brand perks
    icon: (
      <PerkIcon>
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </PerkIcon>
    ),
    title: 'Get exclusive brand perks',
    body: 'Unlock ambassador-only discounts, giveaways, products, and perks from Rally’s brand partners.',
  },
  {
    // Star — getting featured
    icon: (
      <PerkIcon>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </PerkIcon>
    ),
    title: 'Get featured by Rally',
    body: 'Create great content around your races and voice notes for the chance to be featured and collaborate across Rally’s growing social channels.',
  },
];

const LOOKING_FOR: string[] = [
  'You’re running a race in the next 3 months',
  'You post about running on Social Media — Instagram, TikTok, Facebook, or YouTube',
  'You’ve already used Rally or plan to use it on your upcoming race',
  'You genuinely enjoy sharing the things that make running more fun and meaningful',
];

const FAQS: { question: string; answer: string }[] = [
  {
    question: 'Do I need a big following?',
    answer:
      'No. We want to work with runners who are genuinely excited about Rally and want to share it with their community.',
  },
  {
    question: 'How much time does this take?',
    answer:
      'A few posts around the races you’re already running, plus the occasional check-in with us. No quotas, no content calendar, no scripts.',
  },
  {
    question: 'What do I actually have to do?',
    answer:
      'Use Rally on race day, share it with your online community, and help us spread the word about Rally.',
  },
  {
    question: 'When will I hear back?',
    answer:
      'We review applications every week and email everyone either way — even if it’s a no for now.',
  },
  {
    question: 'What if I was declined?',
    answer:
      'You can apply again! We’re still a very young new company and evolving every month. We will open up the program to more runners on a rolling basis as we continue to grow.'
  },
  {
    question: 'I have a question that isn’t answered here.',
    answer:
      'Email us at hello@runwithrally.com or DM us on Instagram or TikTok. A real person replies.',
  },
];

// ── FAQ accordion ────────────────────────────────────────────────────────────

const AmbFAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="amb-faq-item">
      <button
        className={`amb-faq-question${isOpen ? ' amb-faq-question--active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        <span className="amb-faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="amb-faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const AmbassadorsPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    track('ambassador_page_viewed');
  }, []);

  // Lock background scroll while the questionnaire is open.
  useEffect(() => {
    document.body.style.overflow = showForm ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showForm]);

  const openForm = (source: string) => {
    track('ambassador_apply_started', { source });
    setShowForm(true);
  };

  return (
    <div className="amb-page">
      {/* Nav */}
      <div className="amb-nav">
        <div className="amb-nav-container">
          <a href="/" className="amb-nav-logo">
            <img src="/rally-font.svg" alt="Rally" className="amb-rally-logo" />
          </a>
          <div className="amb-nav-stores">
            <a
              href="https://apps.apple.com/us/app/rally-voice-notes-on-race-day/id6746744579"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/app-store-dark.svg" alt="Download on the App Store" className="amb-store-badge" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.wimlabsllc.rallyapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/google-play-badge.png" alt="Get it on Google Play" className="amb-store-badge" />
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="amb-hero">
        <h1 className="amb-hero-title">Ambassador Program</h1>
        <p className="amb-hero-sub">
          Run with Rally. Share it with your community. 
          Help us bring the voices that matter most to more runners on race day.
        </p>
        <button
          className="amb-btn amb-btn--primary amb-btn--hero amb-btn--shine"
          onClick={() => openForm('hero')}
        >
          Apply to be an ambassador
        </button>
        <p className="amb-hero-note">Takes about two minutes</p>
      </section>

      <div className="amb-container">
        {/* Perks */}
        <section className="amb-perks-section">
          <h2 className="amb-section-title">
            Why become an <span className="amb-title-accent">ambassador</span>?
          </h2>
          <div className="amb-perks-grid">
            {PERKS.map(perk => (
              <div className="amb-perk" key={perk.title}>
                {perk.icon}
                <h3 className="amb-perk-title">{perk.title}</h3>
                <p className="amb-perk-body">{perk.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who we're looking for */}
        <section className="amb-looking-section">
          <h2 className="amb-section-title">Who we’re looking for</h2>
          <ul className="amb-looking-list">
            {LOOKING_FOR.map(item => (
              <li className="amb-looking-item" key={item}>
                <span className="amb-check">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="amb-faq-section">
          <h2 className="amb-section-title">FAQ</h2>
          <div className="amb-faq-container">
            {FAQS.map(faq => (
              <AmbFAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="amb-cta-section">
          <h2 className="amb-cta-title">Ready to rally?</h2>
          <p className="amb-cta-sub">
            Tell us about yourself and the races you’ve got coming up.
          </p>
          <button className="amb-btn amb-btn--primary" onClick={() => openForm('footer')}>
            Apply now
          </button>
        </section>
      </div>

      {showForm && <AmbassadorApplyForm onClose={() => setShowForm(false)} />}
    </div>
  );
};

export default AmbassadorsPage;
